/**
 * Hangrendszer — minden hang szintetizált, egyetlen hangminta nélkül.
 *
 * A Doom hangkészlete lényegében három recept variációja:
 *   lövés   = zajimpulzus + gyorsan lecsengő burkoló + szűrősöprés
 *   szörny  = mély, FM-modulált oszcillátor + torzítás
 *   gépezet = szűrt zaj lassú burkolóval
 * Ezt kódból hitelesen elő lehet állítani, és cserébe nulla letöltés,
 * nulla licencgond, és minden hang pontosan olyan hosszú, amilyennek kell.
 *
 * Gazda: C
 */

import * as THREE from 'three'
import { Rng } from '../core/rng'

const SAMPLE_RATE = 44100

// ---------------------------------------------------------------------------
// DSP-primitívek (offline, Float32Array-en)
// ---------------------------------------------------------------------------

interface EnvSpec {
  attack: number
  decay: number
  sustain: number
  release: number
  /** A lecsengés görbülete: 1 = lineáris, >1 = gyorsan induló. */
  curve?: number
}

function envelopeAt(t: number, dur: number, e: EnvSpec): number {
  const curve = e.curve ?? 2
  if (t < e.attack) return Math.pow(t / Math.max(1e-5, e.attack), 0.6)
  const afterAttack = t - e.attack
  if (afterAttack < e.decay) {
    const k = afterAttack / Math.max(1e-5, e.decay)
    return 1 + (e.sustain - 1) * Math.pow(k, 1 / curve)
  }
  const releaseStart = dur - e.release
  if (t >= releaseStart) {
    const k = (t - releaseStart) / Math.max(1e-5, e.release)
    return e.sustain * Math.pow(Math.max(0, 1 - k), curve)
  }
  return e.sustain
}

/** Egypólusú aluláteresztő. `cutoff` 0..1 (a Nyquist arányában). */
class LowPass {
  private z = 0
  process(x: number, cutoff: number): number {
    const a = Math.max(0.0005, Math.min(0.999, cutoff))
    this.z += a * (x - this.z)
    return this.z
  }
}

/** Egypólusú felüláteresztő. */
class HighPass {
  private z = 0
  process(x: number, cutoff: number): number {
    const a = Math.max(0.0005, Math.min(0.999, cutoff))
    this.z += a * (x - this.z)
    return x - this.z
  }
}

/** Lágy telítés — ettől lesz „hangos" és nyers a hang, nem steril. */
function softClip(x: number, drive: number): number {
  const d = x * drive
  return Math.tanh(d) / Math.tanh(drive || 1)
}

interface SynthCtx {
  /** Minta index. */
  i: number
  /** Idő másodpercben. */
  t: number
  /** Teljes hossz. */
  dur: number
  rng: Rng
  lp: LowPass
  lp2: LowPass
  hp: HighPass
}

type Voice = (c: SynthCtx) => number

function render(dur: number, seed: number, voice: Voice): Float32Array {
  const n = Math.max(1, Math.floor(dur * SAMPLE_RATE))
  const out = new Float32Array(n)
  const ctx: SynthCtx = {
    i: 0,
    t: 0,
    dur,
    rng: new Rng(seed),
    lp: new LowPass(),
    lp2: new LowPass(),
    hp: new HighPass(),
  }
  for (let i = 0; i < n; i++) {
    ctx.i = i
    ctx.t = i / SAMPLE_RATE
    out[i] = voice(ctx)
  }
  // Normalizálás — így minden hang azonos fejtérrel indul, és a keverés
  // egyetlen hangerő-szorzóval hangolható.
  let peak = 0
  for (let i = 0; i < n; i++) peak = Math.max(peak, Math.abs(out[i]))
  if (peak > 1e-6) {
    const k = 0.92 / peak
    for (let i = 0; i < n; i++) out[i] *= k
  }
  // Rövid ki-fade, hogy ne kattanjon a vége.
  const fade = Math.min(220, Math.floor(n * 0.1))
  for (let i = 0; i < fade; i++) out[n - 1 - i] *= i / fade
  return out
}

// ---------------------------------------------------------------------------
// Hangreceptek
// ---------------------------------------------------------------------------

const SFX: Record<string, () => Float32Array> = {
  // --- Fegyverek ---------------------------------------------------------
  pistol: () =>
    render(0.19, 101, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.0004, decay: 0.06, sustain: 0.06, release: 0.09, curve: 3 })
      const noise = c.rng.range(-1, 1)
      const body = Math.sin(2 * Math.PI * (180 - 120 * c.t * 6) * c.t)
      const crack = c.hp.process(noise, 0.55) * 1.2
      return softClip((crack * 0.7 + body * 0.5) * env, 2.4)
    }),

  shotgun: () =>
    render(0.5, 202, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.001, decay: 0.14, sustain: 0.08, release: 0.3, curve: 2.6 })
      const noise = c.rng.range(-1, 1)
      // Széles, mély dörrenés: alacsonyra szűrt zaj + zuhanó szinusz.
      const low = c.lp.process(noise, 0.06 + 0.25 * Math.exp(-c.t * 14))
      const boom = Math.sin(2 * Math.PI * (95 - 60 * Math.min(1, c.t * 4)) * c.t)
      const crack = c.hp.process(noise, 0.7) * Math.exp(-c.t * 40)
      return softClip((low * 2.2 + boom * 0.8 + crack * 0.5) * env, 2.0)
    }),

  chaingun: () =>
    render(0.13, 303, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.0003, decay: 0.045, sustain: 0.05, release: 0.06, curve: 3 })
      const noise = c.rng.range(-1, 1)
      const mid = c.lp.process(noise, 0.35)
      const body = Math.sin(2 * Math.PI * (240 - 180 * c.t * 8) * c.t)
      return softClip((mid * 1.4 + body * 0.6) * env, 2.8)
    }),

  rocketFire: () =>
    render(0.65, 404, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.006, decay: 0.1, sustain: 0.35, release: 0.4, curve: 1.6 })
      const noise = c.rng.range(-1, 1)
      const hiss = c.hp.process(c.lp.process(noise, 0.25), 0.1)
      const thump = Math.sin(2 * Math.PI * 70 * c.t) * Math.exp(-c.t * 12)
      return softClip((hiss * 1.5 + thump * 1.6) * env, 1.8)
    }),

  plasmaFire: () =>
    render(0.22, 505, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.0008, decay: 0.05, sustain: 0.1, release: 0.14, curve: 2.4 })
      // FM: a modulátor gyorsan lehangolódik → „energiakisülés" karakter.
      const mf = 420 * Math.exp(-c.t * 9)
      const mod = Math.sin(2 * Math.PI * mf * c.t)
      const car = Math.sin(2 * Math.PI * 760 * c.t + mod * 6)
      const zap = c.hp.process(c.rng.range(-1, 1), 0.6) * Math.exp(-c.t * 30)
      return softClip((car * 0.9 + zap * 0.4) * env, 1.6)
    }),

  explosion: () =>
    render(1.5, 606, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.002, decay: 0.35, sustain: 0.18, release: 0.95, curve: 2.2 })
      const noise = c.rng.range(-1, 1)
      const rumble = c.lp.process(noise, 0.018 + 0.1 * Math.exp(-c.t * 6))
      const sub = Math.sin(2 * Math.PI * (58 - 34 * Math.min(1, c.t * 1.5)) * c.t)
      const crack = c.hp.process(noise, 0.5) * Math.exp(-c.t * 22)
      return softClip((rumble * 3.2 + sub * 1.4 + crack * 0.7) * env, 1.7)
    }),

  emptyClick: () =>
    render(0.09, 707, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.0002, decay: 0.02, sustain: 0.02, release: 0.05, curve: 3 })
      const click = c.hp.process(c.rng.range(-1, 1), 0.8)
      return click * env * 0.7
    }),

  weaponSwitch: () =>
    render(0.2, 808, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.004, decay: 0.06, sustain: 0.25, release: 0.1, curve: 2 })
      const metal = c.hp.process(c.lp.process(c.rng.range(-1, 1), 0.5), 0.35)
      const clack = Math.sin(2 * Math.PI * (620 + 340 * c.t) * c.t) * Math.exp(-c.t * 18)
      return (metal * 0.8 + clack * 0.6) * env
    }),

  // --- Ellenségek --------------------------------------------------------
  sightGrunt: () =>
    render(0.42, 909, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.02, decay: 0.1, sustain: 0.55, release: 0.24, curve: 1.6 })
      // Torzított emberi kiáltás-utánzat: formáns-szerű két szinusz.
      const f0 = 150 + 40 * Math.sin(c.t * 22)
      const v = Math.sin(2 * Math.PI * f0 * c.t) + 0.6 * Math.sin(2 * Math.PI * f0 * 2.4 * c.t)
      const rasp = c.rng.range(-1, 1) * 0.35
      return softClip((v + rasp) * env, 2.6)
    }),

  sightImp: () =>
    render(0.7, 1010, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.05, decay: 0.2, sustain: 0.6, release: 0.4, curve: 1.4 })
      const mf = 62 + 26 * Math.sin(c.t * 7)
      const mod = Math.sin(2 * Math.PI * mf * c.t)
      const car = Math.sin(2 * Math.PI * (128 + 30 * Math.sin(c.t * 3)) * c.t + mod * 4.5)
      return softClip(car * env, 3.2)
    }),

  sightHound: () =>
    render(0.5, 1111, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.01, decay: 0.12, sustain: 0.5, release: 0.3, curve: 1.8 })
      const growl = Math.sin(2 * Math.PI * (95 + 55 * Math.sin(c.t * 34)) * c.t)
      const rasp = c.lp.process(c.rng.range(-1, 1), 0.2)
      return softClip((growl * 1.1 + rasp * 0.9) * env, 3.4)
    }),

  sightSentinel: () =>
    render(0.9, 1212, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.1, decay: 0.25, sustain: 0.65, release: 0.5, curve: 1.3 })
      // Gépi felpörgés: emelkedő fűrészjel-szerű spektrum.
      const f = 90 + 150 * Math.min(1, c.t * 1.4)
      let s = 0
      for (let k = 1; k <= 5; k++) s += Math.sin(2 * Math.PI * f * k * c.t) / k
      return softClip(s * 0.6 * env, 2.0)
    }),

  sightBrute: () =>
    render(1.3, 1313, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.12, decay: 0.3, sustain: 0.7, release: 0.7, curve: 1.2 })
      const mf = 34 + 14 * Math.sin(c.t * 3.5)
      const mod = Math.sin(2 * Math.PI * mf * c.t)
      const car = Math.sin(2 * Math.PI * 62 * c.t + mod * 7)
      const sub = Math.sin(2 * Math.PI * 41 * c.t)
      return softClip((car * 1.2 + sub * 0.9) * env, 3.8)
    }),

  enemyPain: () =>
    render(0.28, 1414, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.004, decay: 0.08, sustain: 0.3, release: 0.16, curve: 2.4 })
      const f = 260 * Math.exp(-c.t * 3)
      const v = Math.sin(2 * Math.PI * f * c.t) + 0.5 * Math.sin(2 * Math.PI * f * 1.9 * c.t)
      return softClip((v + c.rng.range(-0.4, 0.4)) * env, 3)
    }),

  enemyDeath: () =>
    render(0.95, 1515, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.008, decay: 0.25, sustain: 0.35, release: 0.6, curve: 1.7 })
      const f = 190 * Math.exp(-c.t * 1.9)
      const v = Math.sin(2 * Math.PI * f * c.t + Math.sin(2 * Math.PI * f * 0.5 * c.t) * 3)
      const gurgle = c.lp.process(c.rng.range(-1, 1), 0.12) * (0.5 + 0.5 * Math.sin(c.t * 30))
      return softClip((v * 1.1 + gurgle * 1.2) * env, 3)
    }),

  melee: () =>
    render(0.22, 1616, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.001, decay: 0.05, sustain: 0.12, release: 0.13, curve: 2.8 })
      const whoosh = c.lp.process(c.rng.range(-1, 1), 0.3 * Math.exp(-c.t * 6) + 0.05)
      const thud = Math.sin(2 * Math.PI * 130 * c.t) * Math.exp(-c.t * 25)
      return softClip((whoosh * 1.3 + thud * 1.1) * env, 2.2)
    }),

  telegraph: () =>
    render(0.3, 1717, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.02, decay: 0.05, sustain: 0.7, release: 0.15, curve: 1.5 })
      // Emelkedő hang: az agy „valami mindjárt történik"-ként olvassa.
      const f = 300 + 700 * (c.t / c.dur)
      return Math.sin(2 * Math.PI * f * c.t) * env * 0.55
    }),

  // --- Játékos -----------------------------------------------------------
  playerPain: () =>
    render(0.35, 1818, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.005, decay: 0.1, sustain: 0.4, release: 0.2, curve: 2 })
      const f = 210 * Math.exp(-c.t * 2.2)
      const v = Math.sin(2 * Math.PI * f * c.t) + 0.4 * Math.sin(2 * Math.PI * f * 2.7 * c.t)
      return softClip((v + c.rng.range(-0.25, 0.25)) * env, 2.2)
    }),

  playerDeath: () =>
    render(1.8, 1919, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.01, decay: 0.5, sustain: 0.4, release: 1.2, curve: 1.4 })
      const f = 175 * Math.exp(-c.t * 1.1)
      const v = Math.sin(2 * Math.PI * f * c.t + Math.sin(2 * Math.PI * f * 0.4 * c.t) * 4)
      const air = c.lp.process(c.rng.range(-1, 1), 0.1) * Math.exp(-c.t * 0.8)
      return softClip((v * 1.2 + air * 1.4) * env, 2.6)
    }),

  hitMarker: () =>
    render(0.07, 2020, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.0005, decay: 0.02, sustain: 0.1, release: 0.04, curve: 3 })
      return Math.sin(2 * Math.PI * 1750 * c.t) * env * 0.4
    }),

  hitMarkerKill: () =>
    render(0.16, 2121, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.0005, decay: 0.05, sustain: 0.15, release: 0.09, curve: 2.6 })
      // Két hang lefelé: „elesett" jelentésű zenei gesztus.
      const f = c.t < 0.05 ? 1500 : 1000
      return Math.sin(2 * Math.PI * f * c.t) * env * 0.42
    }),

  // --- Tárgyak és gépezetek ----------------------------------------------
  pickupItem: () =>
    render(0.24, 2222, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.002, decay: 0.06, sustain: 0.3, release: 0.14, curve: 2 })
      const step = Math.floor(c.t / 0.05)
      const f = 620 * Math.pow(1.26, step)
      return (Math.sin(2 * Math.PI * f * c.t) * 0.6 + Math.sin(2 * Math.PI * f * 2 * c.t) * 0.2) * env
    }),

  pickupWeapon: () =>
    render(0.42, 2323, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.004, decay: 0.1, sustain: 0.4, release: 0.25, curve: 1.8 })
      const step = Math.floor(c.t / 0.07)
      const f = 220 * Math.pow(1.5, Math.min(3, step))
      const metal = c.hp.process(c.rng.range(-1, 1), 0.5) * Math.exp(-c.t * 9)
      return (Math.sin(2 * Math.PI * f * c.t) * 0.8 + metal * 0.5) * env
    }),

  pickupKey: () =>
    render(0.55, 2424, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.006, decay: 0.14, sustain: 0.45, release: 0.3, curve: 1.6 })
      const f = 520
      const shimmer = Math.sin(2 * Math.PI * f * c.t) + 0.5 * Math.sin(2 * Math.PI * f * 1.5 * c.t) + 0.3 * Math.sin(2 * Math.PI * f * 2.25 * c.t)
      return shimmer * 0.4 * env
    }),

  doorOpen: () =>
    render(1.0, 2525, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.05, decay: 0.15, sustain: 0.7, release: 0.4, curve: 1.3 })
      // Nehéz gépezet: alacsony zaj + lassú, „csikorgó" moduláció.
      const grind = c.lp.process(c.rng.range(-1, 1), 0.05 + 0.03 * Math.sin(c.t * 40))
      const motor = Math.sin(2 * Math.PI * 48 * c.t) * 0.5
      return softClip((grind * 3 + motor) * env, 1.9)
    }),

  doorLocked: () =>
    render(0.35, 2626, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.002, decay: 0.06, sustain: 0.25, release: 0.2, curve: 2.4 })
      const clunk = Math.sin(2 * Math.PI * 110 * c.t) * Math.exp(-c.t * 14)
      const buzz = Math.sin(2 * Math.PI * 220 * c.t) * (c.t > 0.12 && c.t < 0.26 ? 1 : 0)
      return softClip((clunk * 1.4 + buzz * 0.5) * env, 2)
    }),

  lift: () =>
    render(1.4, 2727, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.1, decay: 0.2, sustain: 0.75, release: 0.5, curve: 1.2 })
      const hum = Math.sin(2 * Math.PI * 62 * c.t) * 0.7 + Math.sin(2 * Math.PI * 93 * c.t) * 0.3
      const rattle = c.lp.process(c.rng.range(-1, 1), 0.08) * 1.4
      return softClip((hum + rattle) * env, 1.6)
    }),

  secret: () =>
    render(0.8, 2828, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.01, decay: 0.2, sustain: 0.5, release: 0.45, curve: 1.5 })
      const step = Math.min(3, Math.floor(c.t / 0.11))
      const f = 440 * [1, 1.25, 1.5, 2][step]
      return (Math.sin(2 * Math.PI * f * c.t) * 0.55 + Math.sin(2 * Math.PI * f * 2 * c.t) * 0.2) * env
    }),

  hazardBurn: () =>
    render(0.4, 2929, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.01, decay: 0.1, sustain: 0.4, release: 0.25, curve: 1.8 })
      const sizzle = c.hp.process(c.lp.process(c.rng.range(-1, 1), 0.4), 0.25)
      return sizzle * env * 0.8
    }),

  exitLevel: () =>
    render(1.6, 3030, (c) => {
      const env = envelopeAt(c.t, c.dur, { attack: 0.03, decay: 0.3, sustain: 0.6, release: 0.9, curve: 1.3 })
      const step = Math.min(4, Math.floor(c.t / 0.22))
      const f = 196 * [1, 1.2, 1.5, 1.8, 2.4][step]
      let s = 0
      for (let k = 1; k <= 4; k++) s += Math.sin(2 * Math.PI * f * k * c.t) / (k * 1.5)
      return s * 0.5 * env
    }),
}

export type SfxName = keyof typeof SFX

// ---------------------------------------------------------------------------
// Lejátszó
// ---------------------------------------------------------------------------

export class AudioEngine {
  private ctx: AudioContext | null = null
  private master: GainNode | null = null
  private sfxBus: GainNode | null = null
  private musicBus: GainNode | null = null
  private buffers = new Map<string, AudioBuffer>()
  private listener = { x: 0, y: 0, z: 0, yaw: 0 }
  private music: MusicEngine | null = null
  /** Egy tickben ugyanabból a hangból legfeljebb ennyi indulhat. */
  private recentPlays = new Map<string, number>()

  masterVolume = 0.75
  musicVolume = 0.45
  enabled = true

  /** A böngésző csak felhasználói gesztus után enged hangot. */
  ensureStarted(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume()
      return
    }
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    this.ctx = new Ctor({ sampleRate: SAMPLE_RATE })

    this.master = this.ctx.createGain()
    this.master.gain.value = this.masterVolume
    this.master.connect(this.ctx.destination)

    this.sfxBus = this.ctx.createGain()
    this.sfxBus.gain.value = 1
    this.sfxBus.connect(this.master)

    this.musicBus = this.ctx.createGain()
    this.musicBus.gain.value = this.musicVolume
    this.musicBus.connect(this.master)

    for (const [name, gen] of Object.entries(SFX)) {
      const data = gen()
      const buf = this.ctx.createBuffer(1, data.length, SAMPLE_RATE)
      buf.getChannelData(0).set(data)
      this.buffers.set(name, buf)
    }

    this.music = new MusicEngine(this.ctx, this.musicBus)
  }

  get ready(): boolean {
    return this.ctx !== null
  }

  setListener(pos: THREE.Vector3, yaw: number): void {
    this.listener.x = pos.x
    this.listener.y = pos.y
    this.listener.z = pos.z
    this.listener.yaw = yaw
  }

  play(name: string, pos: THREE.Vector3 | null = null, volume = 1, pitch = 1): void {
    if (!this.enabled || !this.ctx || !this.sfxBus) return
    const buf = this.buffers.get(name)
    if (!buf) return

    // Ugyanaz a hang egy időpillanatban sokszor = fázisrács és fülsértő
    // hangerő. Ezért limitáljuk.
    const now = this.ctx.currentTime
    const last = this.recentPlays.get(name) ?? -1
    if (now - last < 0.012) return
    this.recentPlays.set(name, now)

    let gain = volume
    let pan = 0

    if (pos) {
      const dx = pos.x - this.listener.x
      const dy = pos.y - this.listener.y
      const dz = pos.z - this.listener.z
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz)
      // Lineáris-négyzetes keverék csillapítás: közel nem robban, távol elhal.
      const atten = 1 / (1 + dist * 0.12 + dist * dist * 0.006)
      gain *= atten
      if (gain < 0.004) return

      // Sztereó pozicionálás a hallgató fejirányához képest.
      if (dist > 0.001) {
        const cos = Math.cos(-this.listener.yaw)
        const sin = Math.sin(-this.listener.yaw)
        const rx = dx * cos - dz * sin
        pan = Math.max(-1, Math.min(1, (rx / dist) * 0.85))
      }
    }

    const src = this.ctx.createBufferSource()
    src.buffer = buf
    src.playbackRate.value = pitch

    const g = this.ctx.createGain()
    g.gain.value = gain

    const panner = this.ctx.createStereoPanner()
    panner.pan.value = pan

    src.connect(g).connect(panner).connect(this.sfxBus)
    src.start()
  }

  /** A zene intenzitása 0..1 — a harci nyomás alapján. */
  setThreat(level: number): void {
    this.music?.setThreat(level)
  }

  startMusic(): void {
    this.music?.start()
  }

  stopMusic(): void {
    this.music?.stop()
  }

  setMasterVolume(v: number): void {
    this.masterVolume = v
    if (this.master) this.master.gain.value = v
  }

  setMusicVolume(v: number): void {
    this.musicVolume = v
    if (this.musicBus) this.musicBus.gain.value = v
  }
}

// ---------------------------------------------------------------------------
// Procedurális zene — rétegzett ipari ambient
// ---------------------------------------------------------------------------

/**
 * Két réteg: egy állandó, nyomasztó drón, és egy ritmikus harci réteg, ami
 * a fenyegetettséggel úszik be. Ez sokkal többet ad a hangulathoz, mint
 * amennyibe kerül — és nincs szükség zeneszerzőre.
 */
class MusicEngine {
  private ctx: AudioContext
  private out: GainNode
  private droneGain: GainNode
  private combatGain: GainNode
  private nodes: AudioScheduledSourceNode[] = []
  private timer = 0
  private running = false
  private threat = 0
  private step = 0
  private rng = new Rng(0xbeef)

  constructor(ctx: AudioContext, out: GainNode) {
    this.ctx = ctx
    this.out = out
    this.droneGain = ctx.createGain()
    this.droneGain.gain.value = 0
    this.droneGain.connect(out)
    this.combatGain = ctx.createGain()
    this.combatGain.gain.value = 0
    this.combatGain.connect(out)
  }

  start(): void {
    if (this.running) return
    this.running = true
    const ctx = this.ctx

    // Drón: három enyhén elhangolt oszcillátor egy mély kvint-akkordon.
    const filter = ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = 420
    filter.Q.value = 1.2
    filter.connect(this.droneGain)

    for (const [freq, detune] of [
      [55, 0],
      [55, -7],
      [82.4, 5],
      [110, 3],
    ] as [number, number][]) {
      const osc = ctx.createOscillator()
      osc.type = 'sawtooth'
      osc.frequency.value = freq
      osc.detune.value = detune
      const g = ctx.createGain()
      g.gain.value = 0.14
      osc.connect(g).connect(filter)
      osc.start()
      this.nodes.push(osc)
    }

    // Lassú szűrő-lengetés: a drón "lélegzik", nem áll egy helyben.
    const lfo = ctx.createOscillator()
    lfo.frequency.value = 0.06
    const lfoGain = ctx.createGain()
    lfoGain.gain.value = 180
    lfo.connect(lfoGain).connect(filter.frequency)
    lfo.start()
    this.nodes.push(lfo)

    this.droneGain.gain.setTargetAtTime(0.5, ctx.currentTime, 3)
    this.scheduleLoop()
  }

  private scheduleLoop(): void {
    if (!this.running) return
    const beat = 0.34
    this.timer = window.setTimeout(() => {
      this.tickStep()
      this.scheduleLoop()
    }, beat * 1000)
  }

  private tickStep(): void {
    const ctx = this.ctx
    this.step = (this.step + 1) % 16

    // A harci réteg csak fenyegetettségnél szólal meg.
    const target = this.threat
    this.combatGain.gain.setTargetAtTime(target * 0.55, ctx.currentTime, 1.2)
    if (target < 0.05) return

    const t = ctx.currentTime

    // Lüktető basszus a nyolcadokon
    if (this.step % 4 === 0) {
      const osc = ctx.createOscillator()
      osc.type = 'square'
      osc.frequency.setValueAtTime(48, t)
      osc.frequency.exponentialRampToValueAtTime(34, t + 0.22)
      const g = ctx.createGain()
      g.gain.setValueAtTime(0.5, t)
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.26)
      osc.connect(g).connect(this.combatGain)
      osc.start(t)
      osc.stop(t + 0.3)
    }

    // Fémes ütés a kontraütemen
    if (this.step % 8 === 4) {
      const len = Math.floor(0.18 * ctx.sampleRate)
      const buf = ctx.createBuffer(1, len, ctx.sampleRate)
      const d = buf.getChannelData(0)
      for (let i = 0; i < len; i++) {
        d[i] = this.rng.range(-1, 1) * Math.pow(1 - i / len, 3)
      }
      const src = ctx.createBufferSource()
      src.buffer = buf
      const hp = ctx.createBiquadFilter()
      hp.type = 'highpass'
      hp.frequency.value = 2600
      const g = ctx.createGain()
      g.gain.value = 0.3
      src.connect(hp).connect(g).connect(this.combatGain)
      src.start(t)
    }

    // Ritka, disszonáns motívum a feszültség csúcsán
    if (this.threat > 0.6 && this.step === 12 && this.rng.chance(0.5)) {
      const osc = ctx.createOscillator()
      osc.type = 'triangle'
      const f = this.rng.pick([311, 370, 415, 466])
      osc.frequency.setValueAtTime(f, t)
      const g = ctx.createGain()
      g.gain.setValueAtTime(0.001, t)
      g.gain.exponentialRampToValueAtTime(0.18, t + 0.05)
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.9)
      osc.connect(g).connect(this.combatGain)
      osc.start(t)
      osc.stop(t + 1)
    }
  }

  setThreat(level: number): void {
    this.threat = Math.max(0, Math.min(1, level))
  }

  stop(): void {
    this.running = false
    clearTimeout(this.timer)
    this.droneGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.6)
    this.combatGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.6)
    for (const n of this.nodes) {
      try {
        n.stop(this.ctx.currentTime + 1.5)
      } catch {
        // Már leállt — nem gond.
      }
    }
    this.nodes.length = 0
    void this.out
  }
}
